import { Children, type ReactNode } from "react";

export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3 pb-4">
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-sm text-fg-muted">{eyebrow}</div>}
        <h1 className="truncate text-2xl font-semibold text-fg">{title}</h1>
        {description && <p className="mt-0.5 text-sm text-fg-muted">{description}</p>}
      </div>
      {actions && <div className="flex min-w-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/** A bordered region with a compact header; used for forms and detail groups (not decorative cards). */
export function Section({
  title,
  description,
  actions,
  children,
  tour,
  id,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  tour?: string;
  id?: string;
}) {
  return (
    <section id={id} data-tour={tour} aria-labelledby={id ? `${id}-title` : undefined} className="min-w-0 rounded-lg border border-line bg-surface">
      <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <h2 id={id ? `${id}-title` : undefined} className="text-base font-semibold text-fg">
            {title}
          </h2>
          {description && <p className="mt-0.5 text-sm text-fg-muted">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      <div className="px-4 py-4">{children}</div>
    </section>
  );
}

export function DescriptionList({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 @md:grid-cols-[max-content_1fr]">
      {items.map((item) => (
        <div key={item.label} className="contents">
          <dt className="text-sm text-fg-muted">{item.label}</dt>
          <dd className="min-w-0 text-sm break-words text-fg">{item.value || <span className="text-fg-subtle">—</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Page content in two columns once the page is wide (one column each child, in order), so forms use
 * the width without stretching single fields across it. Each column is its own container: the
 * fields inside size to the column, not the page.
 */
export function Columns({ children }: { children: ReactNode }) {
  return (
    <div className="grid items-start gap-4 @5xl:grid-cols-2">
      {Children.toArray(children).map((column, i) => (
        <div key={i} className="@container flex min-w-0 flex-col gap-4">
          {column}
        </div>
      ))}
    </div>
  );
}
